import { useState, useEffect, useCallback, useMemo } from 'react'
import { message, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import actions from '../../../../../../../../../store/actions'
import { upload as createLibraryItem } from '../../../../../../../../../services/api/library'
import { api } from '../../../../../../../../../services'
import imageCompression from 'browser-image-compression'
import nanoid from 'nanoid'
import { types } from '../../../../../../../../../constants/builder'
import React from 'react'
import {
  FiCamera,
  FiBox,
  FiUser,
  FiLayers,
  FiLayout,
  FiMonitor,
  FiPenTool,
  FiTarget,
  FiZap,
  FiCloudLightning
} from 'react-icons/fi'

export const useMatchImage = ({
  onCancel,
  previewData,
  pageStructureData,
  lessonUpdate,
  pages,
  lessonTitle,
  currentPage,
  upload,
  pageBuilder,
  courseId = null,
  lessonId = null
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { handleLessonInput } = actions.lessonBuilder

  const IMAGE_STYLES = useMemo(
    () => [
      {
        label: t('matchimage:style_photorealistic_label'),
        value: 'photorealistic',
        prompt: t('matchimage:style_photorealistic_prompt'),
        icon: <FiCamera />
      },
      {
        label: t('matchimage:style_product_label'),
        value: 'product',
        prompt: t('matchimage:style_product_prompt'),
        icon: <FiBox />
      },
      {
        label: t('matchimage:style_lifestyle_label'),
        value: 'lifestyle',
        prompt: t('matchimage:style_lifestyle_prompt'),
        icon: <FiUser />
      },
      {
        label: t('matchimage:style_corporate_portrait_label'),
        value: 'corporate_portrait',
        prompt: t('matchimage:style_corporate_portrait_prompt'),
        icon: <FiUser />
      },
      {
        label: t('matchimage:style_3d_modern_label'),
        value: '3d_modern',
        prompt: t('matchimage:style_3d_modern_prompt'),
        icon: <FiBox />
      },
      {
        label: t('matchimage:style_3d_isometric_label'),
        value: '3d_isometric',
        prompt: t('matchimage:style_3d_isometric_prompt'),
        icon: <FiLayers />
      },
      {
        label: t('matchimage:style_flat_corporate_label'),
        value: 'flat_corporate',
        prompt: t('matchimage:style_flat_corporate_prompt'),
        icon: <FiLayout />
      },
      {
        label: t('matchimage:style_ui_brand_label'),
        value: 'ui_brand',
        prompt: t('matchimage:style_ui_brand_prompt'),
        icon: <FiMonitor />
      },
      {
        label: t('matchimage:style_editorial_label'),
        value: 'editorial',
        prompt: t('matchimage:style_editorial_prompt'),
        icon: <FiPenTool />
      },
      {
        label: t('matchimage:style_technical_diagram_label'),
        value: 'technical_diagram',
        prompt: t('matchimage:style_technical_diagram_prompt'),
        icon: <FiTarget />
      },
      {
        label: t('matchimage:style_abstract_corporate_label'),
        value: 'abstract_corporate',
        prompt: t('matchimage:style_abstract_corporate_prompt'),
        icon: <FiZap />
      },
      {
        label: t('matchimage:style_saas_marketing_label'),
        value: 'saas_marketing',
        prompt: t('matchimage:style_saas_marketing_prompt'),
        icon: <FiCloudLightning />
      }
    ],
    [t]
  )

  const [selectedStyle, setSelectedStyle] = useState(IMAGE_STYLES[0].value)
  const [refImages, setRefImages] = useState([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [generationResults, setGenerationResults] = useState([])
  const [currentlyGeneratingIdx, setCurrentlyGeneratingIdx] = useState(null)
  const [isApplied, setIsApplied] = useState(false)
  const userInfo = useSelector(state => state.user.info)

  const getExistingPageImages = useCallback(page => {
    const images = (page?.data || []).filter(
      item =>
        item.type === types.IMAGE ||
        item.type === types.BACKGROUND_IMAGE ||
        item.type === types.BACKGROUND_IMAGE_AUTO
    )
    return images.map(img => ({
      url: img.params?.content,
      id: img.params?.content_id
    }))
  }, [])

  const fetchReferenceImage = useCallback(async ({ url, id }) => {
    try {
      let blob = null
      let base64 = ''

      if (id) {
        const bufferRes = await api.library.getLibraryBuffer(id)
        if (bufferRes?.data) {
          base64 = bufferRes.data
          const byteChars = atob(base64)
          const byteNumbers = new Array(byteChars.length)
          for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          blob = new Blob([byteArray], { type: 'image/jpeg' })
        }
      }

      if (!base64 && url) {
        const res = await fetch(url)
        if (res.ok) {
          blob = await res.blob()
          base64 = await new Promise(resolve => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result.split(',')[1])
            reader.readAsDataURL(blob)
          })
        }
      }

      if (base64) {
        return {
          id: id || Date.now() + Math.random(),
          preview: url || `data:image/jpeg;base64,${base64}`,
          base64,
          mime: blob?.type || 'image/jpeg',
          sizeMB: blob ? (blob.size / (1024 * 1024)).toFixed(2) : '0.00'
        }
      }
    } catch (e) {
      console.error('Failed to fetch reference image', e)
    }
    return null
  }, [])

  useEffect(() => {
    const discoverInitialRefs = async () => {
      const selectedIndices = previewData
        .map((p, i) => (p.pageSelected ? i : -1))
        .filter(i => i !== -1)

      const discovered = []
      for (const idx of selectedIndices) {
        if (discovered.length >= 20) break
        const page = previewData[idx]
        const imagesInfo = getExistingPageImages(page)

        for (const imgInfo of imagesInfo) {
          if (discovered.length >= 20) break
          if (imgInfo?.url) {
            const existingIdx = discovered.findIndex(
              d => d.preview === imgInfo.url
            )
            if (existingIdx === -1) {
              discovered.push({
                id: imgInfo.id || `pending-${nanoid(5)}`,
                preview: imgInfo.url,
                base64: null,
                mime: 'image/jpeg',
                isFetching: false,
                pageIndex: idx,
                selected: true
              })
            }
          }
        }
      }

      if (discovered.length > 0) {
        setRefImages(prev => {
          const existingUrls = new Set(prev.map(img => img.preview))
          const newRefs = discovered.filter(d => !existingUrls.has(d.preview))
          return [...prev, ...newRefs].slice(0, 20)
        })
      }
    }
    discoverInitialRefs()
  }, [previewData, getExistingPageImages])

  const handleRemoveRefImage = id => {
    setRefImages(prev => prev.filter(img => img.id !== id))
  }

  const toggleRefSelection = id => {
    setRefImages(prev =>
      prev.map(img =>
        img.id === id ? { ...img, selected: !img.selected } : img
      )
    )
  }

  const extractPagePrompt = (page, index) => {
    let pageText = ''
      ; (page.data || []).forEach(element => {
        if (element.type === 'TEXT' && element.display !== 'HEADING') {
          const textContent =
            new DOMParser().parseFromString(element.params.content, 'text/html')
              .body.textContent || ''
          pageText += textContent + ' '
        }
      })

    const trimmedText = pageText.trim()
    if (trimmedText) return trimmedText

    const pageTitle = page?.title || page?.params?.title
    if (pageTitle) return pageTitle

    return lessonTitle || `${t('matchimage:page')} ${index + 1}`
  }

  const toDataUrlFromBlob = blob =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = () => reject(new Error('Failed to read image data'))
      reader.readAsDataURL(blob)
    })

  const startMultiPageGeneration = async () => {
    setIsGenerating(true)
    setProgress(0)
    setGenerationResults([])
    setCurrentlyGeneratingIdx(null)
    setIsApplied(false)

    const selectedPagesIndices = previewData
      .map((page, idx) => (page.pageSelected ? idx : -1))
      .filter(idx => idx !== -1)

    if (selectedPagesIndices.length === 0) {
      message.warning(t('matchimage:select_at_least_one_page'))
      setIsGenerating(false)
      return
    }

    const totalSelected = selectedPagesIndices.length
    const results = []
    let lastGeneratedImageBase64 = null
    let lastGeneratedMime = null

    const totalTargetMB = 3
    const perImageTargetMB = totalTargetMB / 9

    let globalRefImages = []
    const selectedRefs = refImages.filter(img => img.selected)
    if (selectedRefs.length > 0) {
      globalRefImages = await Promise.all(
        selectedRefs.map(async (img, index) => {
          try {
            let base64 = img.base64
            let mime = img.mime

            if (!base64) {
              const fullRef = await fetchReferenceImage({
                url: img.preview,
                id: img.id.toString().startsWith('pending-') ? null : img.id
              })
              if (fullRef) {
                base64 = fullRef.base64
                mime = fullRef.mime
              }
            }

            if (!base64) return null

            const dataUrl = `data:${mime};base64,${base64}`
            const file = await imageCompression.getFilefromDataUrl(
              dataUrl,
              `ref-${index}.png`
            )
            const compressed = await imageCompression(file, {
              maxSizeMB: perImageTargetMB,
              maxWidthOrHeight: 800,
              useWebWorker: true
            })
            const compressedBase64 = await imageCompression.getDataUrlFromFile(
              compressed
            )
            return {
              data: compressedBase64.split(',')[1],
              mime_type: compressed.type || 'image/png'
            }
          } catch (e) {
            return img.base64 ? { data: img.base64, mime_type: img.mime } : null
          }
        })
      )
      globalRefImages = globalRefImages.filter(img => img !== null)
    }

    const stylePrompt =
      IMAGE_STYLES.find(s => s.value === selectedStyle)?.prompt || ''
    const negativeConstraints =
      'Avoid unnecessary decorative text, distorted typography, or unrelated symbols. Essential labels or relevant text content from the subject description are permitted but should be clear and professional.'
    const referenceInstructions =
      "Maintain strict visual and stylistic consistency with the provided references (lighting, color palette, mood, and character style). However, crucially, ensure the content and specific meaning of the current page's subject are preserved and correctly depicted. Do not let reference styles override the core message of the page."

    for (let i = 0; i < totalSelected; i++) {
      const pageIdx = selectedPagesIndices[i]
      setCurrentlyGeneratingIdx(pageIdx)
      const page = previewData[pageIdx]
      const prompt = extractPagePrompt(page, pageIdx)

      setGenerationResults(prev => {
        const exists = prev.find(p => p.pageIndex === pageIdx)
        if (exists) return prev
        return [...prev, { pageIndex: pageIdx, status: 'generating' }]
      })

      try {
        const inline_images = []
        inline_images.push(
          ...globalRefImages.map(img => ({
            data: img.data,
            mime_type: img.mime_type || 'image/png'
          }))
        )

        const pageImages = getExistingPageImages(page)
        for (const existingImage of pageImages) {
          if (
            existingImage?.url &&
            !refImages.some(r => r.preview === existingImage.url)
          ) {
            const ref = await fetchReferenceImage({
              url: existingImage.url,
              id: existingImage.id
            })
            if (ref && ref.base64) {
              try {
                const dataUrl = `data:${ref.mime || 'image/jpeg'};base64,${ref.base64
                  }`
                const file = await imageCompression.getFilefromDataUrl(
                  dataUrl,
                  `page-ref-${pageIdx}.png`
                )
                const compressed = await imageCompression(file, {
                  maxSizeMB: perImageTargetMB,
                  maxWidthOrHeight: 800,
                  useWebWorker: true
                })
                const compressedBase64 = await imageCompression.getDataUrlFromFile(
                  compressed
                )
                inline_images.push({
                  data: compressedBase64.split(',')[1],
                  mime_type: compressed.type || 'image/png'
                })
              } catch (e) {
                inline_images.push({
                  data: ref.base64,
                  mime_type: ref.mime || 'image/jpeg'
                })
              }
            }
          }
        }

        if (pageIdx > 0 && !lastGeneratedImageBase64) {
          const prevPage = previewData[pageIdx - 1]
          const prevPageImages = getExistingPageImages(prevPage)
          if (prevPageImages.length > 0) {
            const existingImage = prevPageImages[0]
            if (existingImage?.url) {
              const ref = await fetchReferenceImage({
                url: existingImage.url,
                id: existingImage.id
              })
              if (ref && ref.base64) {
                try {
                  const dataUrl = `data:${ref.mime || 'image/jpeg'};base64,${ref.base64
                    }`
                  const file = await imageCompression.getFilefromDataUrl(
                    dataUrl,
                    `prev-page-ref-${pageIdx - 1}.png`
                  )
                  const compressed = await imageCompression(file, {
                    maxSizeMB: perImageTargetMB,
                    maxWidthOrHeight: 800,
                    useWebWorker: true
                  })
                  const compressedBase64 = await imageCompression.getDataUrlFromFile(
                    compressed
                  )
                  inline_images.push({
                    data: compressedBase64.split(',')[1],
                    mime_type: compressed.type || 'image/png'
                  })
                } catch (e) {
                  inline_images.push({
                    data: ref.base64,
                    mime_type: ref.mime || 'image/jpeg'
                  })
                }
              }
            }
          }
        }

        if (lastGeneratedImageBase64) {
          try {
            const dataUrl = `data:${lastGeneratedMime ||
              'image/png'};base64,${lastGeneratedImageBase64}`
            const file = await imageCompression.getFilefromDataUrl(
              dataUrl,
              `prev-gen-${pageIdx}.png`
            )
            const compressed = await imageCompression(file, {
              maxSizeMB: perImageTargetMB,
              maxWidthOrHeight: 800,
              useWebWorker: true
            })
            const compressedBase64 = await imageCompression.getDataUrlFromFile(
              compressed
            )
            inline_images.push({
              data: compressedBase64.split(',')[1],
              mime_type: compressed.type || 'image/png'
            })
          } catch (e) {
            inline_images.push({
              data: lastGeneratedImageBase64,
              mime_type: lastGeneratedMime || 'image/png'
            })
          }
        }

        const payload = {
          generate_infographic: false,
          type: 'course',
          user_prompt: `
                      ${stylePrompt}
                      
                      Subject / Scene:
                      ${prompt}
                      
                      References:
                      ${referenceInstructions}
                      
                      Negative constraints:
                      ${negativeConstraints}
                      
                      Quality rules:
                      - Professional quality
                      - Clean composition
                      - No watermarks
                      - No distorted or misspelled text
                      - Maintain continuity with previous page visuals while accurately reflecting the current scene's text and meaning
                      `.trim(),
          aspect_ratio: '1:1',
          inline_images: inline_images.slice(-9),
          course_id: courseId,
          org_id: userInfo?.active_organization_id,
          user_id: userInfo?.id
        }

        const filename = `nanobanana-ai-match-${pageIdx + 1}-${Date.now()}.png`
        const response = await api.imageGeneration.generateNanoBananaImage(
          payload
        )
        let imageBlob = null
        let finalDataUrl = ''
        let directLibraryMeta = null

        if (response?.data) {
          const isBlob =
            response.data instanceof Blob ||
            (response.data.type &&
              typeof response.data.size === 'number' &&
              !response.data.body)

          if (isBlob) {
            imageBlob = response.data
            if (imageBlob.type === 'application/json') {
              const text = await new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsText(imageBlob)
              })

              try {
                const json = JSON.parse(text)
                if (json.errorMessage) throw new Error(json.errorMessage)
                const pendingMeta = json.key
                  ? {
                      key: json.key,
                      filename:
                        json.filename || json.key.split('/').pop() || filename,
                      mime_type: json.mime_type || null
                    }
                  : null
                if (json.url) {
                  const imgRes = await fetch(json.url)
                  imageBlob = await imgRes.blob()
                }
                if (pendingMeta?.key) {
                  directLibraryMeta = {
                    key: pendingMeta.key,
                    filename: pendingMeta.filename,
                    type:
                      pendingMeta.mime_type ||
                      imageBlob.type ||
                      'image/jpeg'
                  }
                }
              } catch (e) {
                console.error('Failed to parse JSON response', e)
                throw new Error('Invalid API response format')
              }
            }

            finalDataUrl = await toDataUrlFromBlob(imageBlob)
          } else if (response.data.url) {
            const imgRes = await fetch(response.data.url)
            imageBlob = await imgRes.blob()
            finalDataUrl = await toDataUrlFromBlob(imageBlob)
            if (response.data.key) {
              directLibraryMeta = {
                key: response.data.key,
                filename:
                  response.data.filename ||
                  response.data.key.split('/').pop() ||
                  filename,
                type:
                  response.data.mime_type ||
                  imageBlob.type ||
                  'image/jpeg'
              }
            }
          } else if (response.data.body || response.data.errorMessage) {
            const { body, headers, errorMessage } = response.data
            if (errorMessage) throw new Error(errorMessage)

            const contentType = headers?.['Content-Type'] || 'image/png'
            finalDataUrl = `data:${contentType};base64,${body}`

            const byteChars = atob(body)
            const byteNumbers = new Array(byteChars.length)
            for (let i = 0; i < byteChars.length; i++) {
              byteNumbers[i] = byteChars.charCodeAt(i)
            }
            const byteArray = new Uint8Array(byteNumbers)
            imageBlob = new Blob([byteArray], { type: contentType })
          } else {
            throw new Error('Unexpected response format')
          }
        }

        let uploadedData = null
        if (directLibraryMeta?.key) {
          const createPayload = {
            filename: directLibraryMeta.filename || filename,
            type: directLibraryMeta.type || imageBlob.type || 'image/png',
            key: directLibraryMeta.key,
            format: 'IMAGE',
            status: 'private',
            parent_id: null,
            is_ai_image: true,
            ...(courseId ? { course_id: Number(courseId) } : {})
          }
          const created = await createLibraryItem(createPayload)
          uploadedData = created?.data
        } else {
          const uploadPayload = {
            name: filename,
            is_ai_image: true,
            filename,
            title: filename,
            size: imageBlob.size,
            croppedImage: finalDataUrl,
            type: imageBlob.type || 'image/png',
            lastModifiedDate: new Date().toISOString()
          }

          uploadedData = await upload(uploadPayload, {
            title: filename,
            isPublic: false
          })
        }

        const finalUrl = uploadedData?.link || finalDataUrl
        const contentId =
          uploadedData?.id || Math.floor(1000000 + Math.random() * 9000000)

        lastGeneratedImageBase64 = finalDataUrl.split(',')[1]
        lastGeneratedMime = imageBlob.type || 'image/png'

        results.push({
          pageIndex: pageIdx,
          url: finalUrl,
          contentId: contentId,
          status: 'success'
        })
      } catch (error) {
        console.error(`Error generating image for page ${pageIdx}:`, error)
        results.push({ pageIndex: pageIdx, status: 'error' })
      }

      const newProgress = Math.round(((i + 1) / totalSelected) * 100)
      setProgress(newProgress)
      setCurrentlyGeneratingIdx(null)
      setGenerationResults([...results])
    }

    setCurrentlyGeneratingIdx(null)
    setIsGenerating(false)
  }

  const applyToPages = () => {
    if (!generationResults.length) return

    Modal.confirm({
      title: t('matchimage:apply_to_all_pages'),
      content: t('matchimage:apply_confirmation_content'),
      okText: t('buttons:confirm'),
      cancelText: t('buttons:cancel'),
      centered: true,
      onOk: () => {
        const newPagesLandscape = pageStructureData.map((page, pageIdx) => {
          const res = generationResults.find(
            r => r.pageIndex === pageIdx && r.status === 'success'
          )
          if (!res) return page

          const pageData = [...(page.data || [])]
          let targetUid = null

          if (pageIdx === currentPage - 1 && pageBuilder?.uid) {
            const selectedItem = pageData.find(
              item => item.uid === pageBuilder.uid
            )
            const isImage = selectedItem && selectedItem.type === types.IMAGE
            if (isImage) {
              targetUid = pageBuilder.uid
            }
          }

          if (!targetUid) {
            const existingImage = pageData.find(
              item => item.type === types.IMAGE
            )
            if (existingImage) {
              targetUid = existingImage.uid
            }
          }

          if (targetUid) {
            const updatedData = pageData.map(item => {
              if (item.uid === targetUid) {
                return {
                  ...item,
                  params: {
                    ...item.params,
                    content: res.url,
                    content_id: res.contentId
                  }
                }
              }
              return item
            })
            return { ...page, data: updatedData }
          } else {
            const newUid = nanoid(8)
            const newPageData = pageData.map(item => {
              if (item.display === 'HEADING') {
                return {
                  ...item,
                  params: {
                    ...item.params,
                    x: 40,
                    y: 40,
                    width: 461.5,
                    height: 67
                  }
                }
              } else if (item.display === 'CONTENT') {
                return {
                  ...item,
                  params: {
                    ...item.params,
                    x: 40,
                    y: 127,
                    width: 461.5,
                    height: 385
                  }
                }
              }
              return item
            })
            const newData = [
              ...newPageData,
              {
                uid: newUid,
                type: types.IMAGE,
                display: 'IMAGE',
                params: {
                  content: res.url,
                  content_id: res.contentId,
                  x: 521.5,
                  y: 40,
                  width: 461.5,
                  height: 472
                }
              }
            ]
            return { ...page, data: newData }
          }
        })

        dispatch(handleLessonInput('pages_landscape', newPagesLandscape))
        dispatch(handleLessonInput('pages', pages))

        lessonUpdate({
          pages: pages,
          pages_landScape: newPagesLandscape
        })

        setIsApplied(true)
        message.success(t('matchimage:apply_success'))
        setTimeout(onCancel, 1500)
      }
    })
  }

  return {
    IMAGE_STYLES,
    selectedStyle,
    setSelectedStyle,
    refImages,
    handleRemoveRefImage,
    toggleRefSelection,
    isGenerating,
    progress,
    generationResults,
    currentlyGeneratingIdx,
    isApplied,
    startMultiPageGeneration,
    applyToPages,
    t
  }
}
